import { useId } from 'react';

/**
 * The interface's own icons.
 *
 * Inline SVG rather than an icon font or a sprite: each one inherits `currentColor`, costs no
 * request, and cannot be the thing that fails to load offline. They are ours, not imported content,
 * so there is no untrusted markup anywhere near this file.
 *
 * Every icon is drawn on a 24×24 grid and marked `aria-hidden`. The button around it carries the
 * name, because an icon that has to be described twice is described inconsistently.
 */

const base = {
  viewBox: '0 0 24 24',
  'aria-hidden': true,
  focusable: false,
} as const;

/** Text size: one letterform at two sizes, which is the whole idea in one mark. */
export function TypographyIcon() {
  return (
    <svg {...base} className="icon" fill="none">
      <path
        d="M3 19 7.8 6h1.9L14.5 19"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M5.4 14.6h6.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path
        d="M15.6 19 18.6 11h1.2l3 8"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M16.9 16.4h4.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Theme: a sun and a moon sharing the circle.
 *
 * One mark for the control rather than one per state — the button says which theme is *chosen*
 * through its popover, and a control whose icon changes under you is hard to find twice.
 */
export function ThemeIcon() {
  return (
    <svg {...base} className="icon" fill="none">
      <path
        d="M12 3a9 9 0 0 0 0 18Z"
        fill="currentColor"
      />
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M12 6.5a5.5 5.5 0 0 1 0 11"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Focus: the corners closing in on what is left. */
export function FocusIcon() {
  return (
    <svg {...base} className="icon" fill="none">
      <path
        d="M4 8.5V5.5A1.5 1.5 0 0 1 5.5 4h3M15.5 4h3A1.5 1.5 0 0 1 20 5.5v3M20 15.5v3a1.5 1.5 0 0 1-1.5 1.5h-3M8.5 20h-3A1.5 1.5 0 0 1 4 18.5v-3"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <circle cx="12" cy="12" r="3" fill="currentColor" />
    </svg>
  );
}

/**
 * Flags, drawn rather than typed.
 *
 * The obvious implementation is a regional-indicator emoji — 🇧🇷 — and it is the wrong one:
 * Windows has no flag glyphs at all and renders those as the letters "BR". A flag that shows up as
 * two letters on the most common desktop platform is not a flag, so these are drawn.
 *
 * Simplified for a 20px circle. The point is recognition at a glance, not heraldry.
 */
function FlagFrame({ children }: { readonly children: React.ReactNode }) {
  // The popover shows every flag at once, so the clip path needs an id of its own. Two elements
  // sharing one id is invalid markup, and which one wins is not something to leave to the browser.
  const clipId = useId();

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="icon icon--flag">
      <defs>
        <clipPath id={clipId}>
          <circle cx="12" cy="12" r="11" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>{children}</g>
      {/* A hairline in the text colour, so the flag reads as a control in either theme. */}
      <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeOpacity="0.25" />
    </svg>
  );
}

function FlagBR() {
  return (
    <FlagFrame>
      <rect x="0" y="0" width="24" height="24" fill="#009b3a" />
      <path d="M12 3.5 22 12l-10 8.5L2 12Z" fill="#fedf00" />
      <circle cx="12" cy="12" r="4" fill="#002776" />
    </FlagFrame>
  );
}

function FlagDE() {
  return (
    <FlagFrame>
      <rect x="0" y="0" width="24" height="8" fill="#000000" />
      <rect x="0" y="8" width="24" height="8" fill="#dd0000" />
      <rect x="0" y="16" width="24" height="8" fill="#ffce00" />
    </FlagFrame>
  );
}

function FlagGB() {
  return (
    <FlagFrame>
      <rect x="0" y="0" width="24" height="24" fill="#012169" />
      <path d="M0 0 24 24M24 0 0 24" stroke="#ffffff" strokeWidth="5" />
      <path d="M0 0 24 24M24 0 0 24" stroke="#c8102e" strokeWidth="3" />
      <path d="M12 0v24M0 12h24" stroke="#ffffff" strokeWidth="8" />
      <path d="M12 0v24M0 12h24" stroke="#c8102e" strokeWidth="5" />
    </FlagFrame>
  );
}

const FLAGS: Record<string, () => React.ReactElement> = {
  en: FlagGB,
  pt: FlagBR,
  de: FlagDE,
};

/** The flag for a language, or a globe when a future language has none drawn yet. */
export function FlagIcon({ locale }: { readonly locale: string }) {
  const Flag = FLAGS[locale];
  if (Flag === undefined) {
    return (
      <svg {...base} className="icon" fill="none">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
        <path
          d="M3 12h18M12 3c2.5 2.4 3.8 5.5 3.8 9S14.5 18.6 12 21c-2.5-2.4-3.8-5.5-3.8-9S9.5 5.4 12 3Z"
          stroke="currentColor"
          strokeWidth="1.6"
        />
      </svg>
    );
  }
  return <Flag />;
}

/** A tick, for the chosen row inside a popover. */
export function CheckIcon() {
  return (
    <svg {...base} className="icon icon--check" fill="none">
      <path
        d="M5 12.5 10 17.5 19 7"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
